<template>
  <v-container class="grey lighten-5">
    <v-breadcrumbs :items="breadCrumbs">
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item @click="clickBreadCrumb(item.text)" :disabled="item.disabled" :link="true">
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>
    <div v-if="moveMember!==null">
      <SuperAdminCompanySettings @cancel="cancelMovement" @move="move" :movement="true"/>
    </div>
    <div v-if="selectedUser === null">
      <v-card v-if="moveMember === null && superAdmin" style="margin-bottom: 24px">
        <v-card-text>
          <v-card-title>
            Klicke auf Alle Nutzer Suchen für das komplette Set, oder gib einen Suchbegriff ein, um ihn gezielt zu
            suchen.
          </v-card-title>
          <v-text-field
              v-model="searchTermSuperAdmin"
              label="Suchbegriff für Online-Suche"
              required
              style="margin-right: 16px;"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn class="ma-1" color="primary" @click.stop="loadDataWithSearchTerm(searchTermSuperAdmin)">Gezielte Online Suche
            starten
          </v-btn>
        </v-card-actions>
        <v-btn text @click.stop="loadData">
          Alle Nutzer suchen (Dauert Lange)
          <template v-slot:loader>
        <span class="custom-loader">
          <v-icon light>mdi-cached</v-icon>
        </span>
          </template>
        </v-btn>
      </v-card>

      <h2 v-if="moveMember === null">Nutzerverwaltung</h2>
      <h2 v-else>Nutzer verschieben</h2>
      <br/>

      <div v-if="moveMember===null">
        <v-switch v-if="loading" loading="primary" :input-value="loadInactiveMembers"
                  label="Inklusive inaktiven Nutzern" @change="triggerLoadMembers"/>
        <v-switch v-else :input-value="loadInactiveMembers" label="Inklusive inaktiven Nutzern"
                  @change="triggerLoadMembers"/>
      </div>

      <v-data-table v-if="moveMember === null"
                    :headers="headers"
                    :items="members"
                    :search="search"
                    :items-per-page="15"
                    class="elevation-1"
      >
        <template v-slot:top>
          <v-text-field
              append-icon="mdi-magnify"
              v-model="search"
              single-line
              label="Suche in gefundenen Nutzern"
              class="mx-4"
          />
        </template>

        <template v-slot:item.isAdmin="{ item }">
          <v-simple-checkbox
              v-model="item.isAdmin"
              disabled
          />
        </template>

        <template v-slot:item.isActive="{ item }">
          <v-simple-checkbox
              v-model="item.isActive"
              disabled
          />
        </template>

        <template v-if="superAdmin" v-slot:item.latestCourseSyncLabel="{ item }">
          <span v-if="item.hasCourseSyncRisk">{{ item.latestCourseSyncLabel }}</span>
        </template>

        <template v-if="superAdmin" v-slot:item.courseSyncStatus="{ item }">
          <v-chip v-if="item.hasCourseSyncRisk" color="warning" dark small>
            {{ item.courseSyncStatus }}
          </v-chip>
        </template>

        <template v-slot:item.edit="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  @click="openEdit(item)"
                  class="ma-2"
                  outlined
                  x-small
                  fab
                  v-bind="attrs"
                  v-on="on"
                  color="indigo"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

            </template>
            <span>Bearbeiten</span>
          </v-tooltip>
          <v-tooltip v-if="superAdmin" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  @click="startMovement(item)"
                  class="ma-2"
                  outlined
                  x-small
                  fab
                  color="indigo"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-content-save-move</v-icon>
              </v-btn>
            </template>
            <span>Firma ändern</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-btn v-if="moveMember === null" class="ma-1" color="info" @click="createUser">
        Nutzer erstellen
        <template v-slot:loader>
        <span class="custom-loader">
          <v-icon light>mdi-cached</v-icon>
        </span>
        </template>
      </v-btn>
    </div>
    <div v-else>
      <v-btn
          @click="selectedUser = null"
          class="ma-2"
          outlined
          x-small
          color="indigo"
      >
        Zurück
      </v-btn>
      <UserEdit @reload="reload" :userId="selectedUser.id" @finish="onUserCreated" :super-admin="superAdmin"/>
    </div>
    <v-snackbar :timeout="5000" :value="error" color="error" left shaped top app>Fehler</v-snackbar>
    <v-snackbar :timeout="5000" :value="success" color="primary" left shaped top app>Erfolgreich</v-snackbar>
  </v-container>

</template>

<script>
import UserEdit from "@/components/UserEdit";
import User from "@/models/User";
import SuperAdminCompanySettings from "@/components/sites/SuperAdminCompanySettings";

export default {
  name: "MemberSettings",
  components: {SuperAdminCompanySettings, UserEdit},
  data: () => ({
    selectedUser: null,
    loadInactiveMembers: false,
    success: false,
    error: false,
    searchTermSuperAdmin: '',
    loading: false,
    members: [],
    companies: [],
    search: '',
    moveMember: null,
  }),
  computed: {
    breadCrumbs() {
      if (this.selectedUser === null) {
        return [{
          text: "Nutzer", disabled: true
        }]
      } else {
        return [{
          text: "Nutzer", disabled: false, link: true
        }, {
          text: this.selectedUser.id === "-1" ? "Neuer Nutzer" : this.selectedUser.fullName, disabled: true
        }]
      }
    },
    headers() {
      const baseHeaders = [
        {
          text: "Username",
          value: "username",
          align: 'start',
        },
        {text: "E-Mail", value: "email"},
        {text: "Name", value: "name"},
        {text: "Nachname", value: "surname"},
        {text: "Departments", value: "companyDepartments"},
      ];

      if (this.superAdmin) {
        let result = [
          {text: "Company", value: "companyName"},
          ...baseHeaders,
          {text: "Letzter Kurs-Sync", value: "latestCourseSyncLabel"},
          {text: "Sync Status", value: "courseSyncStatus"},
          {text: "Admin", value: "isAdmin"},
          {text: "Aktiv", value: "isActive"},
          {
            text: "Bearbeiten", value: "edit",
            sortable: false,
          },
        ];
        return result;
      } else {
        return [
          ...baseHeaders,
          {text: "Admin", value: "isAdmin"},
          {text: "Aktiv", value: "isActive"},
          {
            text: "Bearbeiten", value: "edit",
            sortable: false,
          },
        ];
      }
    }
  },
  methods: {
    move(company) {
      this.loading = false;
      this.$httpCompanyApi.moveUser(this.moveMember.id, company.id, () => {
        this.loadData()
        this.moveMember = null;
        this.loading = false;
        this.success = true;
        setTimeout(() => this.success = false, 3000);
      }, () => {
        this.error = true;
        this.loading = false;
        setTimeout(() => this.error = false, 3000);
      })
    },
    reload() {
      this.loadData();
    },
    startMovement(user) {
      this.moveMember = user;
    },
    cancelMovement() {
      this.moveMember = null;
    },
    onUserCreated(user) {
      this.members.unshift(user);
      this.selectedUser = null;
    },
    createUser() {
      this.selectedUser = new User({active: true, isAdmin: true})
    },
    clickBreadCrumb(text) {
      if (this.selectedUser && text === "Nutzer") {
        this.selectedUser = null;
        this.loadData();
      }
    },
    openEdit(item) {
      this.selectedUser = item;
    },
    triggerLoadMembers() {
      this.loadInactiveMembers = !this.loadInactiveMembers;
      this.loadData();
    },
    loadDataWithSearchTerm(searchTerm) {
      this.loading = true;
      let result = (result) => {
        this.members = result;
        this.loading = false;
        if (this.superAdmin) {
          this.$httpCompanyApi.all((companies) => {
            this.companies = companies;
          })
        }
      };
      if (searchTerm !== null && searchTerm !== undefined) {
        this.$httpUserApi.companyUsersWithSearchTerm(searchTerm, this.loadInactiveMembers, result);
      }
    },
    loadData() {
      this.loading = true;
      let result = (result) => {
        this.members = result;
        this.loading = false;
        if (this.superAdmin) {
          this.$httpCompanyApi.all((companies) => {
            this.companies = companies;
          })
        }
      };
      this.$httpUserApi.companyUsers(this.loadInactiveMembers, result);
    },
  },
  mounted() {
    if (!this.superAdmin) {
      this.loadData();
    }
  },
  props: {
    superAdmin: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>

</style>
